import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { acceptInvitationApi } from '../../api/invitations';
import '../css/login.css';
import '../../Components/LoginForm/style.css';

export const INVITATION_STATUS = {
  ACCEPTING: 'accepting',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

const DEFAULT_ERROR =
  'This invitation link is no longer valid. Ask your administrator to send a new one.';
const DEFAULT_SUCCESS = 'Your invitation has been accepted. Sign in to start using Bento.';

// Landing page for the invitation link mailed to a user
// (/invitations/accept/:token). It accepts the invitation on arrival and
// reports the outcome; either way the only way on is the login screen. The API
// treats a repeat accept by a member as a success, so a reload (or a mail
// scanner opening the link first) is harmless. Laid out like the login page so
// the two read as one flow.
const InvitationAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(
    token ? INVITATION_STATUS.ACCEPTING : INVITATION_STATUS.REJECTED
  );
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR);
  const [successMessage, setSuccessMessage] = useState(DEFAULT_SUCCESS);
  // React 18 StrictMode mounts effects twice in development; only ever send
  // the token once.
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!token || submittedRef.current) return;
    submittedRef.current = true;

    (async () => {
      try {
        const data = await acceptInvitationApi(token);
        if (typeof data?.message === 'string' && data.message) setSuccessMessage(data.message);
        setStatus(INVITATION_STATUS.ACCEPTED);
      } catch (e) {
        const data = e?.response?.data;
        const message = typeof data === 'string' ? data : data?.message || data?.error;
        setErrorMessage(message || DEFAULT_ERROR);
        setStatus(INVITATION_STATUS.REJECTED);
      }
    })();
  }, [token]);

  const goToLogin = () => navigate('/login');

  const accepted = status === INVITATION_STATUS.ACCEPTED;
  const rejected = status === INVITATION_STATUS.REJECTED;

  let title = 'Accepting your invitation';
  let body = 'Please wait while we add you to your organization.';
  if (accepted) {
    title = 'Invitation Accepted';
    body = successMessage;
  } else if (rejected) {
    title = 'Invitation Not Valid';
    body = errorMessage;
  }

  return (
    <div className="Login">
      <Container>
        <Row>
          <Col>
            <Container>
              <Row className="flex-column mt-2">
                <Col className="Login-info-logo">
                  <img src="/bento-logo.png" alt="Bento Logo" />
                  <span>Bento</span>
                </Col>
                <Col className="Login-info-welcome">Welcome to</Col>
                <h2 className="Login-info-title">Bento</h2>
                <h3 className="Login-info-subtitle">The Benchmarking Tool</h3>
              </Row>
              <Row className="mt-4">
                <Container className="bg-white p-4 m-0 login-form-container">
                  <h4 className="mb-3">{title}</h4>
                  <p className={rejected ? 'text-danger' : undefined}>{body}</p>
                  {accepted || rejected ? (
                    <Button className="bento-btn" onClick={goToLogin}>
                      <span className="button-text">Go to Login</span>
                    </Button>
                  ) : (
                    <Spinner animation="border" role="status" />
                  )}
                </Container>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InvitationAccept;
